import { useState } from 'react';
import { Button, ConfirmDialog } from '@roc/ui';
import { observer } from 'mobx-react';
import { LoanStatus, LoanSubType } from '@roc/feature-utils';
import { useHistory } from 'react-router';
import { usePlatformLoanRoutes } from '@roc/feature-loans-routes-config';
import { useBaseStore } from '@roc/feature-app-core';
import { MoveToExternalCapitalService } from '../services/moveToExternalCapitalService';

const moveToExternalCapitalService = new MoveToExternalCapitalService();

const allowedSubTypes = [
  LoanSubType.FIX_AND_FLIP,
  LoanSubType.GROUND_UP,
  LoanSubType.STABILIZED_BRIDGE,
  LoanSubType.SINGLE_PROPERTY,
  LoanSubType.RENTAL_PORTFOLIO,
];

interface MoveToExternalCapitalButtonProps {
  loanId: string;
  status: string;
  loanSubType: string;
  changedCapitalProvider?: boolean;
}

export const MoveToExternalCapitalButton = observer(
  ({ loanId, status, loanSubType, changedCapitalProvider }: MoveToExternalCapitalButtonProps) => {
    const [open, setOpen] = useState(false);
    const { push } = useHistory();
    const { platformLoanRoutesConfig } = usePlatformLoanRoutes();
    const { globalStore } = useBaseStore();

    if (!globalStore.userFeatures?.platformLoanSubmissionEnabled) {
      return null;
    }

    if (!allowedSubTypes.includes(loanSubType as LoanSubType)) {
      return null;
    }

    if (status !== LoanStatus.DECLINED && status !== LoanStatus.RETRACTED) {
      return null;
    }

    if (changedCapitalProvider) {
      return null;
    }

    const firstPhrase =
      status === LoanStatus.RETRACTED
        ? 'This application was retracted.'
        : 'This application was declined by Platform Capital.';

    const handleConfirm = async () => {
      try {
        await moveToExternalCapitalService.moveToExternalCapital(loanId);
        setOpen(false);
        push(platformLoanRoutesConfig.loans(loanId.toString()).children.dashboard.url);
      } catch {
        globalStore.notificationStore.showErrorNotification({
          message: 'Error moving loan to external capital provider.',
        });
      }
    };

    return (
      <>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpen(true)}
          testId="move-to-external-capital"
        >
          Move to External Capital
        </Button>
        <ConfirmDialog
          open={open}
          title="Move to External Capital"
          body={
            <>
              {firstPhrase}
              <br />
              <br />
              Moving it will unlock the loan record so you can apply to other capital providers.
            </>
          }
          handleCancel={() => setOpen(false)}
          handleClose={() => setOpen(false)}
          handleConfirm={handleConfirm}
          confirmButtonText="Continue"
          cancelButtonText="Cancel"
        />
      </>
    );
  }
);
